/**
 * Sub-Departments Modal Component
 * Manage departments and their sub-departments (trades)
 * 
 * Created: 2026-01-26
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useMsal } from '@azure/msal-react';
import { authenticatedApiCall } from '../../utils/apiClient';
import { useToast } from '../../hooks/useToast';

const SubDepartmentsModal = ({ isOpen, onClose }) => {
  const { instance, accounts } = useMsal();
  const { showToast } = useToast();
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expandedDept, setExpandedDept] = useState(null);
  const [editingSubDept, setEditingSubDept] = useState(null);
  const [newSubDeptName, setNewSubDeptName] = useState('');
  const [newSubDeptDesc, setNewSubDeptDesc] = useState('');
  const [addingToDept, setAddingToDept] = useState(null);
  const [saving, setSaving] = useState(false);

  // Fetch departments with sub-departments
  const fetchDepartments = useCallback(async () => {
    try {
      setLoading(true);
      const response = await authenticatedApiCall(
        instance, accounts, 'GET',
        '/role-templates/departments'
      );
      if (response.data.success) {
        setDepartments(response.data.data || []);
      }
    } catch (err) {
      console.error('Error fetching departments:', err);
      showToast('Failed to load departments', 'error');
    } finally {
      setLoading(false);
    }
  }, [instance, accounts, showToast]);

  useEffect(() => {
    if (isOpen) {
      fetchDepartments();
    }
  }, [isOpen, fetchDepartments]);

  // Add new sub-department
  const handleAddSubDept = async (deptId) => {
    if (!newSubDeptName.trim()) {
      showToast('Name is required', 'error');
      return;
    }

    try {
      setSaving(true);
      const response = await authenticatedApiCall(
        instance, accounts, 'POST',
        `/role-templates/departments/${deptId}/sub-departments`,
        { name: newSubDeptName.trim(), description: newSubDeptDesc.trim() }
      );
      
      if (response.data.success) {
        showToast('Sub-department added', 'success');
        setNewSubDeptName('');
        setNewSubDeptDesc('');
        setAddingToDept(null);
        fetchDepartments();
      }
    } catch (err) {
      console.error('Error adding sub-department:', err);
      showToast('Failed to add sub-department', 'error');
    } finally {
      setSaving(false);
    }
  };

  // Update sub-department
  const handleUpdateSubDept = async () => {
    if (!editingSubDept || !editingSubDept.name.trim()) {
      showToast('Name is required', 'error');
      return;
    }

    try {
      setSaving(true);
      const response = await authenticatedApiCall(
        instance, accounts, 'PUT',
        `/role-templates/sub-departments/${editingSubDept.id}`,
        { name: editingSubDept.name.trim(), description: editingSubDept.description?.trim() || '' }
      );
      
      if (response.data.success) {
        showToast('Sub-department updated', 'success');
        setEditingSubDept(null);
        fetchDepartments();
      }
    } catch (err) {
      console.error('Error updating sub-department:', err);
      showToast('Failed to update sub-department', 'error');
    } finally {
      setSaving(false);
    }
  };

  // Delete sub-department
  const handleDeleteSubDept = async (subDeptId, subDeptName) => {
    if (!window.confirm(`Are you sure you want to delete "${subDeptName}"? This may affect users assigned to this sub-department.`)) {
      return;
    }

    try {
      setSaving(true);
      const response = await authenticatedApiCall(
        instance, accounts, 'DELETE',
        `/role-templates/sub-departments/${subDeptId}`
      );
      
      if (response.data.success) {
        showToast('Sub-department deleted', 'success');
        fetchDepartments();
      }
    } catch (err) {
      console.error('Error deleting sub-department:', err);
      showToast('Failed to delete sub-department', 'error');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.5)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 1000
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        width: '90%',
        maxWidth: '800px',
        maxHeight: '80vh',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: '0 4px 20px rgba(0,0,0,0.15)'
      }}>
        {/* Header */}
        <div style={{
          padding: '20px 24px',
          borderBottom: '1px solid #e9ecef',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <h2 style={{ margin: 0, fontSize: '1.25rem', color: '#333' }}>
            🏢 Departments & Sub-Departments
          </h2>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '1.5rem',
              cursor: 'pointer',
              color: '#666'
            }}
          >
            ×
          </button>
        </div>

        {/* Content */}
        <div style={{ padding: '24px', overflowY: 'auto', flex: 1 }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
              Loading departments...
            </div>
          ) : (
            <div>
              <p style={{ color: '#666', marginBottom: '20px', fontSize: '14px' }}>
                Sub-departments determine which modules and data users can access within each department.
                Click on a department to expand and manage its sub-departments.
              </p>

              {departments.map(dept => (
                <div
                  key={dept.id}
                  style={{
                    border: '1px solid #e9ecef',
                    borderRadius: '8px',
                    marginBottom: '12px',
                    overflow: 'hidden'
                  }}
                >
                  {/* Department Header */}
                  <div
                    onClick={() => setExpandedDept(expandedDept === dept.id ? null : dept.id)}
                    style={{
                      padding: '16px 20px',
                      backgroundColor: expandedDept === dept.id ? '#e7f3ff' : '#f8f9fa',
                      cursor: 'pointer',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      transition: 'background-color 0.2s'
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <span style={{ fontSize: '18px' }}>
                        {expandedDept === dept.id ? '▼' : '▶'}
                      </span>
                      <strong style={{ fontSize: '16px' }}>{dept.name}</strong>
                      <span style={{
                        backgroundColor: '#6c757d',
                        color: 'white',
                        padding: '2px 8px',
                        borderRadius: '12px',
                        fontSize: '12px'
                      }}>
                        {(dept.sub_departments || []).length} sub-depts
                      </span>
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setAddingToDept(dept.id);
                        setExpandedDept(dept.id);
                      }}
                      style={{
                        padding: '6px 12px',
                        backgroundColor: '#28a745',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '13px'
                      }}
                    >
                      + Add
                    </button>
                  </div>

                  {/* Sub-departments List */}
                  {expandedDept === dept.id && (
                    <div style={{ padding: '16px 20px', backgroundColor: 'white' }}>
                      {/* Add new sub-department form */}
                      {addingToDept === dept.id && (
                        <div style={{
                          padding: '12px',
                          backgroundColor: '#f8f9fa',
                          borderRadius: '6px',
                          marginBottom: '16px'
                        }}>
                          <div style={{ marginBottom: '12px' }}>
                            <input
                              type="text"
                              placeholder="Sub-department name"
                              value={newSubDeptName}
                              onChange={(e) => setNewSubDeptName(e.target.value)}
                              style={{
                                width: '100%',
                                padding: '8px 12px',
                                border: '1px solid #ddd',
                                borderRadius: '4px',
                                fontSize: '14px'
                              }}
                            />
                          </div>
                          <div style={{ marginBottom: '12px' }}>
                            <input
                              type="text"
                              placeholder="Description (optional)"
                              value={newSubDeptDesc}
                              onChange={(e) => setNewSubDeptDesc(e.target.value)}
                              style={{
                                width: '100%',
                                padding: '8px 12px',
                                border: '1px solid #ddd',
                                borderRadius: '4px',
                                fontSize: '14px'
                              }}
                            />
                          </div>
                          <div style={{ display: 'flex', gap: '8px' }}>
                            <button
                              onClick={() => handleAddSubDept(dept.id)}
                              disabled={saving}
                              style={{
                                padding: '6px 16px',
                                backgroundColor: '#28a745',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: saving ? 'wait' : 'pointer',
                                fontSize: '13px'
                              }}
                            >
                              {saving ? 'Saving...' : 'Save'}
                            </button>
                            <button
                              onClick={() => {
                                setAddingToDept(null);
                                setNewSubDeptName('');
                                setNewSubDeptDesc('');
                              }}
                              style={{
                                padding: '6px 16px',
                                backgroundColor: '#6c757d',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontSize: '13px'
                              }}
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      )}

                      {/* List of sub-departments */}
                      {(dept.sub_departments || []).length === 0 ? (
                        <p style={{ color: '#999', fontSize: '14px', fontStyle: 'italic', textAlign: 'center' }}>
                          No sub-departments defined. Click "+ Add" to create one.
                        </p>
                      ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                          {(dept.sub_departments || []).map(sd => (
                            <div
                              key={sd.id}
                              style={{
                                padding: '12px 16px',
                                backgroundColor: editingSubDept?.id === sd.id ? '#fff3cd' : '#f8f9fa',
                                borderRadius: '6px',
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center'
                              }}
                            >
                              {editingSubDept?.id === sd.id ? (
                                <div style={{ flex: 1, marginRight: '12px' }}>
                                  <input
                                    type="text"
                                    value={editingSubDept.name}
                                    onChange={(e) => setEditingSubDept({
                                      ...editingSubDept,
                                      name: e.target.value
                                    })}
                                    style={{
                                      width: '100%',
                                      padding: '6px 10px',
                                      border: '1px solid #ddd',
                                      borderRadius: '4px',
                                      fontSize: '14px',
                                      marginBottom: '8px'
                                    }}
                                  />
                                  <input
                                    type="text"
                                    placeholder="Description"
                                    value={editingSubDept.description || ''}
                                    onChange={(e) => setEditingSubDept({
                                      ...editingSubDept,
                                      description: e.target.value
                                    })}
                                    style={{
                                      width: '100%',
                                      padding: '6px 10px',
                                      border: '1px solid #ddd',
                                      borderRadius: '4px',
                                      fontSize: '13px'
                                    }}
                                  />
                                </div>
                              ) : (
                                <div>
                                  <strong style={{ fontSize: '14px' }}>{sd.name}</strong>
                                  {sd.description && (
                                    <p style={{ margin: '4px 0 0', fontSize: '12px', color: '#666' }}>
                                      {sd.description}
                                    </p>
                                  )}
                                </div>
                              )}
                              
                              <div style={{ display: 'flex', gap: '6px' }}>
                                {editingSubDept?.id === sd.id ? (
                                  <>
                                    <button
                                      onClick={handleUpdateSubDept}
                                      disabled={saving}
                                      style={{
                                        padding: '4px 10px',
                                        backgroundColor: '#28a745',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '4px',
                                        cursor: saving ? 'wait' : 'pointer',
                                        fontSize: '12px'
                                      }}
                                    >
                                      Save
                                    </button>
                                    <button
                                      onClick={() => setEditingSubDept(null)}
                                      style={{
                                        padding: '4px 10px',
                                        backgroundColor: '#6c757d',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '4px',
                                        cursor: 'pointer',
                                        fontSize: '12px'
                                      }}
                                    >
                                      Cancel
                                    </button>
                                  </>
                                ) : (
                                  <>
                                    <button
                                      onClick={() => setEditingSubDept({ ...sd })}
                                      style={{
                                        padding: '4px 10px',
                                        backgroundColor: '#007bff',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '4px',
                                        cursor: 'pointer',
                                        fontSize: '12px'
                                      }}
                                    >
                                      Edit
                                    </button>
                                    <button
                                      onClick={() => handleDeleteSubDept(sd.id, sd.name)}
                                      disabled={saving}
                                      style={{
                                        padding: '4px 10px',
                                        backgroundColor: '#dc3545',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '4px',
                                        cursor: 'pointer',
                                        fontSize: '12px'
                                      }}
                                    >
                                      Delete
                                    </button>
                                  </>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{
          padding: '16px 24px',
          borderTop: '1px solid #e9ecef',
          display: 'flex',
          justifyContent: 'flex-end'
        }}>
          <button
            onClick={onClose}
            style={{
              padding: '10px 24px',
              backgroundColor: '#6c757d',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500'
            }}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default SubDepartmentsModal;
